// frontend/js/classViewer.js
// Presenter interface for teaching ZEconomy pedagogical sessions

// ============================================
// Translation Helper
// ============================================

function t(key, fallback) {
  if (window.i18n && typeof window.i18n.t === 'function') {
    const result = window.i18n.t(key);
    return result !== key ? result : fallback;
  }
  return fallback;
}

// Get current language
function getCurrentLang() {
  if (window.i18n && window.i18n.lang) {
    return window.i18n.lang;
  }
  return 'en';
}

// ============================================
// Utility Functions
// ============================================

function getLessonIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('id');
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ============================================
// State
// ============================================

let currentLesson = null;
let currentStepIndex = 0;

// ============================================
// Render Functions
// ============================================

function renderLesson(lesson) {
  currentLesson = lesson;

  // Update page title
  document.title = `${lesson.title} - ${t('classes.presenter_suffix', 'Presenter')}`;

  // Header
  document.getElementById('lesson-title').textContent = lesson.title;
  document.getElementById('lesson-tagline').textContent = lesson.tagline || '';

  const levelText = lesson.level || t('classes.mixed_level', 'mixed level');
  const minutesText = t('classes.minutes', 'minutes');
  document.getElementById('lesson-meta').textContent =
    `${levelText} • ${lesson.durationMinutes || 60} ${minutesText}`;

  // Objectives
  renderObjectives(lesson);

  // Start with first step
  renderStep(0);
}

function renderObjectives(lesson) {
  const objectivesEl = document.getElementById('objectives');

  if (lesson.objectives && lesson.objectives.length) {
    const items = lesson.objectives.map(o => `<li>${escapeHtml(o)}</li>`).join('');
    const objectivesTitle = t('classes.learning_objectives', 'Learning Objectives');
    objectivesEl.innerHTML = `
      <strong>${objectivesTitle}</strong>
      <ul>${items}</ul>
    `;
  } else {
    objectivesEl.style.display = 'none';
  }
}

function renderStep(index) {
  if (!currentLesson || index < 0 || index >= currentLesson.steps.length) return;

  currentStepIndex = index;
  const step = currentLesson.steps[index];

  // Create artwork lookup
  const artworkMap = {};
  (currentLesson.artworks || []).forEach(a => {
    artworkMap[a.id] = a;
  });

  // Clear displays
  const imageDisplay = document.getElementById('image-display');
  const contentArea = document.getElementById('content-area');
  imageDisplay.innerHTML = '';
  contentArea.innerHTML = '';

  // Render based on step type
  switch (step.type) {
    case 'pdf_document':
      renderPdfDocument(step, imageDisplay, contentArea);
      break;
    case 'document_section':
      renderDocumentSection(step, imageDisplay, contentArea);
      break;
    case 'art_compare':
      renderArtCompareStep(step, artworkMap, imageDisplay, contentArea);
      break;
    case 'bridge':
    case 'content':
    case 'activity':
    case 'reflection':
      renderContentStep(step, artworkMap, imageDisplay, contentArea);
      break;
    default:
      renderContentStep(step, artworkMap, imageDisplay, contentArea);
  }

  // Update navigation buttons
  updateNavigation();
}

function renderArtCompareStep(step, artworkMap, imageDisplay, contentArea) {
  // Render side-by-side comparison images
  if (step.artworks && step.artworks.length >= 2) {
    const artwork1 = artworkMap[step.artworks[0]];
    const artwork2 = artworkMap[step.artworks[1]];

    if (artwork1 && artwork2) {
      const viewDetailsText = t('classes.view_details', 'View Details');
      const comparisonHtml = `
        <div class="image-comparison">
          <div class="image-comparison-item">
            ${artwork1.image ? `<img src="${artwork1.image}" alt="${escapeHtml(artwork1.title)}">` : ''}
            <div class="image-caption">
              <strong>${escapeHtml(artwork1.title)}</strong>
              ${escapeHtml(artwork1.artist)}, ${escapeHtml(artwork1.date)}<br>
              ${artwork1.medium ? `${escapeHtml(artwork1.medium)}<br>` : ''}
              ${artwork1.location ? `${escapeHtml(artwork1.location)}<br>` : ''}
              ${artwork1.link ? `<a href="${escapeHtml(artwork1.link)}" target="_blank" rel="noopener">${viewDetailsText}</a>` : ''}
            </div>
          </div>
          <div class="image-comparison-item">
            ${artwork2.image ? `<img src="${artwork2.image}" alt="${escapeHtml(artwork2.title)}">` : ''}
            <div class="image-caption">
              <strong>${escapeHtml(artwork2.title)}</strong>
              ${escapeHtml(artwork2.artist)}, ${escapeHtml(artwork2.date)}<br>
              ${artwork2.medium ? `${escapeHtml(artwork2.medium)}<br>` : ''}
              ${artwork2.location ? `${escapeHtml(artwork2.location)}<br>` : ''}
              ${artwork2.link ? `<a href="${escapeHtml(artwork2.link)}" target="_blank" rel="noopener">${viewDetailsText}</a>` : ''}
            </div>
          </div>
        </div>
      `;
      imageDisplay.innerHTML = comparisonHtml;
    }
  }

  // Render content
  const discussionPointsText = t('classes.discussion_points', 'Discussion Points');
  const contentHtml = `
    <h2>${escapeHtml(step.title)}</h2>
    ${step.prompt ? `
      <div class="presenter-prompt">
        <strong>${discussionPointsText}:</strong>
        <div class="content-text">${escapeHtml(step.prompt)}</div>
      </div>
    ` : ''}
  `;
  contentArea.innerHTML = contentHtml;
}

function renderContentStep(step, artworkMap, imageDisplay, contentArea) {
  // Render single image if artwork is referenced
  if (step.artworks && step.artworks.length > 0) {
    const artwork = artworkMap[step.artworks[0]];
    if (artwork && artwork.image) {
      const viewDetailsText = t('classes.view_details', 'View Details');
      const imageHtml = `
        <div class="image-single">
          <img src="${artwork.image}" alt="${escapeHtml(artwork.title)}">
          <div class="image-caption">
            <strong>${escapeHtml(artwork.title)}</strong>
            ${escapeHtml(artwork.artist)}, ${escapeHtml(artwork.date)}<br>
            ${artwork.medium ? `${escapeHtml(artwork.medium)}<br>` : ''}
            ${artwork.location ? `${escapeHtml(artwork.location)}<br>` : ''}
            ${artwork.link ? `<a href="${escapeHtml(artwork.link)}" target="_blank" rel="noopener">${viewDetailsText}</a>` : ''}
          </div>
        </div>
      `;
      imageDisplay.innerHTML = imageHtml;
    }
  }

  // Render content
  let contentHtml = `<h2>${escapeHtml(step.title)}</h2>`;

  if (step.body) {
    contentHtml += `<div class="content-text">${escapeHtml(step.body)}</div>`;
  }

  if (step.instructions) {
    const activityInstructionsText = t('classes.activity_instructions', 'Activity Instructions');
    contentHtml += `
      <div class="presenter-prompt">
        <strong>${activityInstructionsText}:</strong>
        <div class="content-text">${escapeHtml(step.instructions)}</div>
      </div>
    `;
  }

  if (step.prompt) {
    const discussionPointsText = t('classes.discussion_points', 'Discussion Points');
    contentHtml += `
      <div class="presenter-prompt">
        <strong>${discussionPointsText}:</strong>
        <div class="content-text">${escapeHtml(step.prompt)}</div>
      </div>
    `;
  }

  contentArea.innerHTML = contentHtml;
}

function renderDocumentSection(step, imageDisplay, contentArea) {
  // No images for document sections
  imageDisplay.innerHTML = '';

  // Render HTML content (not escaped - server-generated content)
  let contentHtml = `<h2>${escapeHtml(step.title)}</h2>`;

  // Add the document content (contains HTML with footnotes)
  if (step.body) {
    contentHtml += step.body;
  }

  // Add reading time estimate if available
  if (step.wordCount) {
    const readingMinutes = Math.ceil(step.wordCount / 150);
    const estimatedReadingText = t('classes.estimated_reading_time', 'Estimated reading time');
    const minText = t('classes.min', 'min');
    contentHtml += `<div class="reading-time">${estimatedReadingText}: ${readingMinutes} ${minText}</div>`;
  }

  contentArea.innerHTML = contentHtml;

  // Scroll to top of content
  contentArea.scrollTop = 0;
}

function renderPdfDocument(step, imageDisplay, contentArea) {
  // No images for PDF documents
  imageDisplay.innerHTML = '';

  // Render PDF viewer
  const downloadPdfText = t('classes.download_pdf', 'Download PDF');
  const contentHtml = `
    <h2>${escapeHtml(step.title)}</h2>
    <div class="pdf-viewer-container">
      <iframe
        src="${escapeHtml(step.pdfUrl)}"
        class="pdf-iframe"
        title="${escapeHtml(step.title)}"
      ></iframe>
      <div class="pdf-download-link">
        <a href="${escapeHtml(step.pdfUrl)}" download target="_blank">
          ${downloadPdfText}
        </a>
      </div>
    </div>
  `;

  contentArea.innerHTML = contentHtml;

  // Scroll to top of content
  contentArea.scrollTop = 0;
}

// ============================================
// Navigation
// ============================================

function updateNavigation() {
  const prevBtn = document.getElementById('prev-step');
  const nextBtn = document.getElementById('next-step');

  prevBtn.disabled = currentStepIndex === 0;
  nextBtn.disabled = !currentLesson || currentStepIndex === currentLesson.steps.length - 1;
}

function setupNavigation() {
  document.getElementById('prev-step').addEventListener('click', () => {
    if (currentStepIndex > 0) {
      renderStep(currentStepIndex - 1);
    }
  });

  document.getElementById('next-step').addEventListener('click', () => {
    if (currentLesson && currentStepIndex < currentLesson.steps.length - 1) {
      renderStep(currentStepIndex + 1);
    }
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft' && currentStepIndex > 0) {
      renderStep(currentStepIndex - 1);
    } else if (e.key === 'ArrowRight' && currentLesson && currentStepIndex < currentLesson.steps.length - 1) {
      renderStep(currentStepIndex + 1);
    }
  });
}

// ============================================
// Load Lesson
// ============================================

async function loadLesson() {
  const id = getLessonIdFromUrl();
  const lang = getCurrentLang();

  if (!id) {
    const noLessonText = t('classes.no_lesson_specified', 'No lesson specified.');
    document.body.innerHTML = `<p style="text-align: center; padding: 3rem;">${noLessonText}</p>`;
    return;
  }

  try {
    const res = await fetch(`/api/classes/${id}?lang=${lang}`);

    if (!res.ok) {
      if (res.status === 404) {
        const notFoundText = t('classes.lesson_not_found', 'Lesson not found.');
        document.body.innerHTML = `<p style="text-align: center; padding: 3rem;">${notFoundText}</p>`;
      } else {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      return;
    }

    const lesson = await res.json();
    renderLesson(lesson);
    setupNavigation();

  } catch (err) {
    console.error('Error loading lesson:', err);
    const errorText = t('classes.load_lesson_error', 'Error loading lesson. Please try again later.');
    document.body.innerHTML = `<p style="text-align: center; padding: 3rem;">${errorText}</p>`;
  }
}

// Wait for i18n to be ready, then load lesson
function init() {
  loadLesson();
  // Re-load lesson on language change to get translated content
  if (window.i18n) {
    document.addEventListener(window.i18n.EVENT_NAME || 'languagechange', () => {
      loadLesson();
    });
  }
}

// Load lesson when page loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
